import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  startMasteryGate,
  completeMasteryGate,
  checkGateEligibility,
  getStudentInfo,
  getGateHistory,
  getAttemptDetails,
  GateAttempt,
  GateAttemptDetails,
} from '../services/api';
import { storage } from '../services/storage';
import { GateIntro } from '../components/mastery-gate/GateIntro';
import { GateQuestion } from '../components/mastery-gate/GateQuestion';
import { GateResults } from '../components/mastery-gate/GateResults';
import { useTimer } from '../hooks/useTimer';

interface GateQuestionData {
  question_id: string;
  prompt: string;
  time_limit_seconds: number;
}

interface GateResponseData {
  question_id: string;
  given_answer: string;
  correct: boolean;
  time_ms: number;
}

type GatePhase = 'intro' | 'active' | 'results';

export const MasteryGatePage: React.FC = () => {
  const { unit } = useParams<{ unit: string }>();
  const unitNum = parseInt(unit || '1', 10);
  const studentId = storage.getStudentId();

  const [phase, setPhase] = useState<GatePhase>('intro');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<GateQuestionData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<GateResponseData[]>([]);
  const [results, setResults] = useState<any>(null);
  const [eligible, setEligible] = useState(false);
  const [weakAreas, setWeakAreas] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState<string>('');
  const [pastAttempts, setPastAttempts] = useState<GateAttempt[]>([]);
  const [viewingAttempt, setViewingAttempt] = useState<GateAttemptDetails | null>(null);

  const timer = useTimer(30 * 60, handleTimeout); // 30 minutes

  useEffect(() => {
    loadInitialData();
  }, [unitNum]);

  async function loadInitialData() {
    try {
      // Fetch student info, eligibility, and history in parallel
      const [eligibilityData, studentData, historyData] = await Promise.all([
        checkGateEligibility(studentId, unitNum),
        getStudentInfo(studentId).catch(() => ({ name: 'Student' })),
        getGateHistory(studentId, unitNum).catch(() => ({ attempts: [] })),
      ]);
      setEligible(eligibilityData.eligible);
      setWeakAreas(eligibilityData.weak_areas || []);
      setStudentName(studentData.name);
      storage.setStudentName(studentData.name);
      setPastAttempts(historyData.attempts || []);
    } catch (err) {
      console.error('Failed to load initial data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleViewAttempt(attemptId: string) {
    try {
      setLoading(true);
      const details = await getAttemptDetails(attemptId, studentId);
      setViewingAttempt(details);
    } catch (err) {
      console.error('Failed to load attempt details:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleBackFromAttempt() {
    setViewingAttempt(null);
  }

  async function handleStart() {
    try {
      setLoading(true);
      const data = await startMasteryGate(studentId, unitNum);
      setSessionId(data.session_id);
      setQuestions(data.questions);
      setPhase('active');
      timer.reset(data.time_limit_minutes * 60);
      timer.start();
    } catch (err: any) {
      alert(err.response?.data?.detail?.message || 'Failed to start gate');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitAnswer(answer: string) {
    const question = questions[currentIndex];
    const timeMs = timer.getElapsedMs();

    // For now, we'll mark correctness on the backend
    // In a real implementation, you'd validate here or on submit
    const response: GateResponseData = {
      question_id: question.question_id,
      given_answer: answer,
      correct: false, // Will be determined by backend
      time_ms: timeMs,
    };

    const newResponses = [...responses, response];
    setResponses(newResponses);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      await submitGate(newResponses);
    }
  }

  async function submitGate(finalResponses: GateResponseData[]) {
    timer.pause();

    try {
      setLoading(true);
      const data = await completeMasteryGate(sessionId!, studentId, finalResponses);
      setResults(data);
      setPhase('results');
    } catch (err) {
      console.error('Failed to submit gate:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleTimeout() {
    submitGate(responses);
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Viewing a past attempt
  if (viewingAttempt) {
    return (
      <div className="viewing-past-attempt">
        <button className="back-btn" onClick={handleBackFromAttempt}>
          ← Back to Gate
        </button>
        <p className="attempt-date-header">
          Attempt from {new Date(viewingAttempt.attempted_at).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
        <GateResults
          passed={viewingAttempt.passed}
          score={viewingAttempt.score}
          total={viewingAttempt.total}
          percentage={viewingAttempt.percentage}
          loBreakdown={viewingAttempt.lo_breakdown}
          weakAreas={viewingAttempt.weak_areas}
          unit={viewingAttempt.unit}
          studentName={studentName}
          questionResults={viewingAttempt.question_results}
        />
      </div>
    );
  }

  if (phase === 'intro') {
    return (
      <GateIntro
        unit={unitNum}
        totalQuestions={20}
        passThreshold={16}
        timeLimitMinutes={30}
        onStart={handleStart}
        eligible={eligible}
        weakAreas={weakAreas}
        pastAttempts={pastAttempts}
        onViewAttempt={handleViewAttempt}
      />
    );
  }

  if (phase === 'active' && questions.length > 0) {
    const currentQuestion = questions[currentIndex];

    return (
      <GateQuestion
        questionNumber={currentIndex + 1}
        totalQuestions={questions.length}
        prompt={currentQuestion.prompt}
        answerType="numeric"
        timeRemaining={timer.timeRemaining}
        onSubmit={handleSubmitAnswer}
        onTimeout={handleTimeout}
      />
    );
  }

  if (phase === 'results' && results) {
    return (
      <GateResults
        passed={results.passed}
        score={results.score}
        total={results.total}
        percentage={results.percentage}
        loBreakdown={results.lo_breakdown}
        weakAreas={results.weak_areas}
        unit={unitNum}
        studentName={studentName}
        questionResults={results.question_results}
      />
    );
  }

  return null;
};
